import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, CardHeader, Col, Row, Spinner } from 'reactstrap'
import { ICategory } from './index'
import { toast } from 'react-toastify'
import DeleteModal from 'Components/Common/DeleteModal'
import { useDebouncedCallback } from 'use-debounce'

interface IProps {
  fetching: boolean
  categoryRecords: ICategory[]
  handleOpenDetails: (arg: string) => void
  setOpenCategoryPopup: (arg: boolean) => void
  setEditMode: (arg: boolean) => void
  handleDeleteCategory: (arg: string) => Promise<string>
}

const CategoriesTable: React.FC<IProps> = ({
  categoryRecords,
  handleOpenDetails,
  setOpenCategoryPopup,
  setEditMode,
  fetching,
  handleDeleteCategory
}) => {
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState('')
  const [filteredRecords, setFilteredRecords] = useState<ICategory[]>(categoryRecords)

  useEffect(() => {
    setFilteredRecords(categoryRecords)
  }, [categoryRecords])

  const handleAddCategory = () => {
    setEditMode(false)
    setOpenCategoryPopup(true)
  }

  const handleEditCategory = (id: string | undefined) => {
    if (id) {
      setEditMode(true)
      setOpenCategoryPopup(true)
      handleOpenDetails(id)
    }
  }

  const handleDeleteRecord = async (id: string | undefined) => {
    if (id) {
      const message = await handleDeleteCategory(id)
      if (message === 'SUCCESSFULL') {
        toast.success('Kategori başarıyla silindi')
      }
    }
  }

  const handleShowModalDelete = (id: string | undefined) => {
    if (id) {
      setShowModalDelete(true)
      setSelectedId(id)
    }
  }

  const handleDelete = () => {
    handleDeleteRecord(selectedId)
    setShowModalDelete(false)
  }

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredRecords(categoryRecords)
    } else {
      const filtered = categoryRecords.filter((cat) =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredRecords(filtered)
    }
  }, 300)

  const handleInputChange = (e: { target: { value: string } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex justify-content-between'>
              <div className='search-box d-inline-block col-8 d-flex align-items-center'>
                <input
                  id='search-bar-category'
                  className='form-control search'
                  placeholder='Kategori ara...'
                  onChange={handleInputChange}
                  value={query}
                />
                <i className='bx bx-search-alt search-icon'></i>
              </div>
              <Button className='p-2' color='primary' onClick={handleAddCategory}>
                <div className='flex align-items-center'>
                  {fetching ? (
                    <Spinner size='sm'> Loading... </Spinner>
                  ) : (
                    <>
                      <i className='mdi mdi-plus'></i>
                      <span className='fw-semibold ms-1'>Yeni Kategori</span>
                    </>
                  )}
                </div>
              </Button>
            </CardHeader>
            <CardBody>
              <div className='table-responsive table-card'>
                <table className='table align-middle table-nowrap table-striped-columns mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th scope='col'>İsim</th>
                      <th scope='col'>Açıklama</th>
                      <th scope='col'>Fiyat (TL)</th>
                      <th scope='col'>Durum</th>
                      <th scope='col' style={{ width: '150px' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((category, index) => (
                      <tr key={index}>
                        <td>
                          <Link to='#' className='fw-medium'>
                            {category.name}
                          </Link>
                        </td>
                        <td>{category.description || '-'}</td>
                        <td>{category.price ? `${category.price} TL` : '-'}</td>
                        <td>
                          <span className={`badge ${category.isActive ? 'bg-success' : 'bg-secondary'}`}>
                            {category.isActive ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td>
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => handleEditCategory(category?._id)}
                          >
                            Düzenle
                          </button>
                          <button
                            type='button'
                            className='btn btn-sm btn-danger ms-2'
                            onClick={() => handleShowModalDelete(category?._id)}
                          >
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredRecords.length === 0 && !fetching && (
                      <tr>
                        <td colSpan={5} className='text-center py-4'>
                          Kategori bulunamadı
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <DeleteModal
        show={showModalDelete}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowModalDelete(false)}
      />
    </React.Fragment>
  )
}

export default CategoriesTable
